import { ArrowUpTrayIcon, DocumentIcon, PencilIcon, XMarkIcon } from "@heroicons/react/24/solid";
import ProgressBar from "components/atoms/ProgressBar";
import { ChangeEventHandler, MouseEventHandler, useEffect, useRef, useState, ReactNode } from "react";
import { humanReadableFileSize } from "utils/helpers";
import { uploadFileToS3 } from "utils/HelperFunctions";
import ErrorMessage from "../../atoms/forms/ErrorMessage";
import Label from "../../atoms/forms/Label";

type Props = {
  label: ReactNode;
  id: string;
  setMediaFile: ChangeEventHandler<HTMLInputElement>;
  selectedFile?: File;
  mediaURL?: string;
  placeHolder: string;
  errorMessage: any;
  isDisabled?: boolean;
  clearSelection: MouseEventHandler;
  onMediaUploadSuccess: (mediaURL: string) => void;
  onMediaUploadError: (error: any) => void;
};

function MediaUploadWithLabel({
  id,
  selectedFile,
  mediaURL,
  label,
  setMediaFile,
  placeHolder,
  errorMessage,
  clearSelection,
  onMediaUploadSuccess,
  onMediaUploadError,
  isDisabled,
}: Props) {
  const [fileUploadingStatus, setFileUploadingStatus] = useState<
    "not-started" | "uploading" | "success" | "failed" | "aborted"
  >("not-started");
  const [progress, setProgress] = useState(0);

  const uploadRequestController = useRef(new AbortController());

  useEffect(() => {
    if (selectedFile) {
      setFileUploadingStatus("not-started");
      setProgress(0);
    }
  }, [selectedFile]);

  const onUploadProgress = (e: ProgressEvent) => {
    setProgress(parseFloat(((e.loaded * 100) / e.total).toFixed(2)));
  };

  const uploadHandler: MouseEventHandler = async (ev) => {
    ev.preventDefault();
    if (selectedFile) {
      try {
        setFileUploadingStatus("uploading");
        uploadRequestController.current = new AbortController();
        const mediaUrl = await uploadFileToS3(selectedFile, uploadRequestController.current.signal, onUploadProgress);
        if (mediaUrl) {
          onMediaUploadSuccess(mediaUrl.file_url);
          setFileUploadingStatus("success");
          setProgress(0);
        }
      } catch (e) {
        setFileUploadingStatus("failed");
        setProgress(0);
        onMediaUploadError(e);
      }
    }
  };

  const abortHandler: MouseEventHandler = (ev) => {
    ev.preventDefault();
    setFileUploadingStatus("aborted");
    setProgress(0);
    uploadRequestController.current.abort();
  };

  return (
    <div className="relative mt-1 rounded-md">
      <div className="flex">
        <Label htmlFor={id} label={label} />
      </div>
      <label
        htmlFor={id}
        className="group my-2 block cursor-text rounded-lg border-1 border-dashed border-neutral-200 p-4 transition-all"
      >
        <div className="flex items-center justify-between">
          <div className="">
            <div
              className="line-clamp-1 break-all text-xs font-light text-gray-100 group-hover:underline"
              title={selectedFile?.name || mediaURL || placeHolder}
            >
              {selectedFile?.name || mediaURL || placeHolder}
            </div>
            <div className="text-base text-gray-300 sm:flex sm:items-center">
              {selectedFile ? humanReadableFileSize(selectedFile?.size) : ""}
            </div>
          </div>
          <div className="ml-6 flex flex-shrink-0 gap-1">
            {fileUploadingStatus !== "uploading" && (
              <>
                {!(selectedFile || mediaURL) && (
                  // eslint-disable-next-line jsx-a11y/label-has-associated-control
                  <label
                    htmlFor={id}
                    className="inline-flex cursor-pointer items-center gap-1 rounded-md bg-accent bg-opacity-90 py-1.5 pl-2 pr-2.5 text-xxs font-medium text-primary shadow-sm ring-1 ring-inset ring-accent hover:bg-opacity-100"
                  >
                    <DocumentIcon className="h-4 w-4" />
                    Select File
                  </label>
                )}
                {selectedFile && (
                  <button
                    disabled={!!errorMessage}
                    onClick={uploadHandler}
                    type="button"
                    className="inline-flex items-center gap-1 rounded-md bg-secondary bg-opacity-90 py-1.5 pl-2 pr-2.5 text-xxs font-medium text-white shadow-sm ring-1 ring-inset ring-secondary hover:bg-opacity-100 disabled:cursor-not-allowed"
                  >
                    <ArrowUpTrayIcon className="h-4 w-4" />
                    Upload
                  </button>
                )}
                {(selectedFile || mediaURL) && (
                  // eslint-disable-next-line jsx-a11y/label-has-associated-control
                  <label
                    title="Change file"
                    htmlFor={id}
                    className="inline-flex cursor-pointer items-center gap-1 rounded-md bg-gray-50 bg-opacity-90 p-2 text-xxs font-medium text-primary shadow-sm ring-1 ring-inset ring-gray-50 hover:bg-opacity-100"
                  >
                    <PencilIcon className="h-4 w-4" />
                  </label>
                )}
                {(mediaURL || selectedFile) && (
                  <button
                    title="Remove"
                    onClick={clearSelection}
                    type="button"
                    className="inline-flex items-center gap-1 rounded-md bg-red-500 bg-opacity-90 p-2 text-xxs font-medium text-white shadow-sm ring-1 ring-inset ring-red-500 hover:bg-opacity-100"
                  >
                    <XMarkIcon className="h-4 w-4" />
                  </button>
                )}
              </>
            )}
            {fileUploadingStatus === "uploading" && (
              <button
                onClick={abortHandler}
                type="button"
                className="inline-flex items-center gap-1 rounded-md bg-red-500 bg-opacity-90 py-1.5 pl-1.5 pr-2.5 text-xxs font-medium text-white shadow-sm ring-1 ring-inset ring-red-500 hover:bg-opacity-100"
              >
                <XMarkIcon className="h-4 w-4" />
                Cancel
              </button>
            )}
          </div>
        </div>
        <input disabled={isDisabled} id={id} name={id} type="file" className="sr-only" onChange={setMediaFile} />
        {fileUploadingStatus !== "not-started" && <ProgressBar progress={progress} status={fileUploadingStatus} />}
      </label>
      <ErrorMessage errorMessage={errorMessage} errorMessageClasses="leading-snug" />
    </div>
  );
}
export default MediaUploadWithLabel;
